import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import PatientNav from './PatientNav';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaPlus, FaSearch, FaPills, FaCalendar, FaStethoscope, FaTimes } from 'react-icons/fa';
import Loader from '../components/Loader';

const Prescription = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPrescriptionDetails, setShowPrescriptionDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { nationalId } = useParams();

  const [newPrescription, setNewPrescription] = useState({
    date: '',
    patient: nationalId,
    doctor: user?.id || '',
    diagnosis: '',
    medications: [{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }],
    notes: '',
    status: 'active',
    refills: 0
  });

  useEffect(() => {
    const fetchPrescriptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/prescriptions/patient/${nationalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPrescriptions(response.data || []);
      } catch (err) {
        setPrescriptions([]);
      } finally {
        setLoading(false);
      }
    };
    if (token && nationalId) fetchPrescriptions();
  }, [nationalId, token]);

  const validateForm = () => {
    if (!newPrescription.date) return 'Date is required';
    if (!newPrescription.diagnosis) return 'Diagnosis is required';
    if (newPrescription.medications.length === 0) return 'At least one medication is required';
    if (newPrescription.medications.some(med => !med.name || !med.dosage || !med.frequency)) {
      return 'All medications must have name, dosage, and frequency';
    }
    return '';
  };

  const handleAddPrescription = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be logged in as a dentist to create prescriptions.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/prescriptions`, newPrescription, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrescriptions([response.data, ...prescriptions]);
      setNewPrescription({
        date: '',
        patient: nationalId,
        doctor: user.id,
        diagnosis: '',
        medications: [{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }],
        notes: '',
        status: 'active',
        refills: 0
      });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create prescription');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPrescription({ ...newPrescription, [name]: value });
  };

  const handleMedicationChange = (index, field, value) => {
    const updatedMedications = [...newPrescription.medications];
    updatedMedications[index][field] = value;
    setNewPrescription({ ...newPrescription, medications: updatedMedications });
  };

  const addMedication = () => {
    setNewPrescription({
      ...newPrescription,
      medications: [...newPrescription.medications, { name: '', dosage: '', frequency: '', duration: '', instructions: '' }]
    });
  };

  const removeMedication = (index) => {
    const updatedMedications = newPrescription.medications.filter((_, i) => i !== index);
    setNewPrescription({ ...newPrescription, medications: updatedMedications });
  };

  const filteredPrescriptions = prescriptions.filter((prescription) =>
    prescription.diagnosis.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prescription.medications.some(med => med.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        return 'Date not available';
      }
      
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'Date not available';
    }
  };

  const deletePrescription = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/prescriptions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrescriptions(prescriptions.filter((prescription) => prescription._id !== id));
      setShowPrescriptionDetails(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete prescription');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || user.role !== 'dentist') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-[#0077B6] mb-4">
                  <FaStethoscope className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600 mb-6">Please log in as a dentist to view this page.</p>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 p-4 bg-red-100 text-[#333333] rounded-lg"
              >
                {error}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Prescriptions
                  </h1>
                  <p className="text-gray-600">Create and manage patient prescriptions</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(true)}
                  className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-2.5 rounded-full font-medium hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  New Prescription
                </motion.button>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative mb-6"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search prescriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-full bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:bg-white sm:text-sm transition-all duration-200"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {filteredPrescriptions.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-8 text-center"
                  >
                    <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
                      <FaPills className="h-16 w-16" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No prescriptions found</h3>
                    <p className="text-gray-500">
                      {searchTerm ? 'Try a different search term' : 'Get started by creating a new prescription'}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddForm(true)}
                      className="mt-6 inline-flex items-center px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium shadow-md hover:shadow-lg"
                    >
                      <FaPlus className="h-5 w-5 mr-2" />
                      Create Prescription
                    </motion.button>
                  </motion.div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredPrescriptions.map((prescription) => (
                      <motion.li
                        key={prescription._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setShowPrescriptionDetails(prescription)}
                      >
                        <div className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-3 w-3 rounded-full bg-[#28A745]"></div>
                              <h3 className="text-lg font-medium text-gray-800">{prescription.diagnosis}</h3>
                            </div>
                            <div className="text-sm text-gray-500">{formatDate(prescription.date)}</div>
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Medications:</span> {prescription.medications?.length || 0} items
                            </p>
                            <div className="flex gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(prescription.status)}`}>
                                {prescription.status}
                              </span>
                              {prescription.refills > 0 && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {prescription.refills} refills
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </motion.div>
            {showAddForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">New Prescription</h2>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleAddPrescription} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date*</label>
                        <div className="relative">
                          <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            name="date"
                            value={newPrescription.date}
                            onChange={handleInputChange}
                            className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Refills</label>
                        <input
                          type="number"
                          name="refills"
                          value={newPrescription.refills}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          min="0"
                          max="5"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Diagnosis*</label>
                      <textarea
                        name="diagnosis"
                        value={newPrescription.diagnosis}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Medications</h3>
                        <button
                          type="button"
                          onClick={addMedication}
                          className="px-3 py-1 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors text-sm"
                        >
                          + Add Medication
                        </button>
                      </div>
                      <div className="space-y-4">
                        {newPrescription.medications.map((medication, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Medication Name*</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Amoxicillin"
                                  value={medication.name}
                                  onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dosage*</label>
                                <input
                                  type="text"
                                  placeholder="e.g., 500mg"
                                  value={medication.dosage}
                                  onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                  required
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency*</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Twice daily"
                                  value={medication.frequency}
                                  onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                                <input
                                  type="text"
                                  placeholder="e.g., 7 days"
                                  value={medication.duration}
                                  onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                              <textarea
                                placeholder="e.g., Take with food"
                                value={medication.instructions}
                                onChange={(e) => handleMedicationChange(index, 'instructions', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                rows="2"
                              />
                            </div>
                            {newPrescription.medications.length > 1 && (
                              <div className="mt-4 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => removeMedication(index)}
                                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                                >
                                  Remove Medication
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={newPrescription.notes}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                      />
                    </div>

                    <div className="flex justify-end space-x-4">
                      <motion.button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium transition-colors shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Create Prescription
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}
            {showPrescriptionDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">Prescription Details</h2>
                    <button
                      onClick={() => setShowPrescriptionDetails(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date</p>
                        <p className="text-sm text-gray-900">{formatDate(showPrescriptionDetails.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(showPrescriptionDetails.status)}`}>
                          {showPrescriptionDetails.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Refills</p>
                        <p className="text-sm text-gray-900">{showPrescriptionDetails.refills || 0}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Diagnosis</p>
                      <p className="text-sm text-gray-900">{showPrescriptionDetails.diagnosis}</p>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Medications</h3>
                      <div className="space-y-4">
                        {showPrescriptionDetails.medications?.map((medication, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-xs font-medium text-gray-500">Medication Name</p>
                                <p className="text-sm font-medium text-gray-900">{medication.name}</p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">Dosage</p>
                                <p className="text-sm text-gray-900">{medication.dosage}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-xs font-medium text-gray-500">Frequency</p>
                                <p className="text-sm text-gray-900">{medication.frequency}</p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">Duration</p>
                                <p className="text-sm text-gray-900">{medication.duration || 'Not specified'}</p>
                              </div>
                            </div>
                            {medication.instructions && (
                              <div>
                                <p className="text-xs font-medium text-gray-500">Instructions</p>
                                <p className="text-sm text-gray-900">{medication.instructions}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {showPrescriptionDetails.notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-sm text-gray-900">{showPrescriptionDetails.notes}</p>
                      </div>
                    )}

                    <div className="flex justify-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => deletePrescription(showPrescriptionDetails._id)}
                        className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-700 font-medium transition-colors shadow-md"
                      >
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Prescription;
